import styled from '@emotion/styled';
import { AnimatePresence } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { ValuesType } from 'utility-types';
import { GAME_MODE, PlayerSetup, SongPreview } from '~/interfaces';
import { Button } from '~/modules/Elements/Button';
import { typography } from '~/modules/Elements/cssMixins';
import InputManager from '~/modules/GameEngine/Input/InputManager';
import gameEvents from '~/modules/GameEvents/GameEvents';
import { useEventEffect } from '~/modules/GameEvents/hooks';
import PlayersManager from '~/modules/Players/PlayersManager';
import RemoteMicManager from '~/modules/RemoteMic/RemoteMicManager';
import { PLAYER_NAMES_SESSION_STORAGE_KEY } from '~/modules/hooks/players/consts';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import storage from '~/modules/utils/storage';
import MicCheck from '~/routes/SingASong/SongSelection/Components/SongSettings/MicCheck';
import SelectInputModal from '~/routes/SelectInput/SelectInputModal';
import inputSourceListManager from '~/routes/SelectInput/InputSources';
import { MicSetupPreferenceSetting, useSettingValue } from '~/routes/Settings/SettingsState';
import SinglePlayer from '~/routes/SingASong/SongSelection/Components/SongSettings/PlayerSettings/SinglePlayer';
import { canStartGameMode, modeNeedsMultiplePlayers } from '~/routes/SingASong/SongSelection/Components/SongSettings/playerModeRequirements';

interface Props {
  songPreview: SongPreview;
  onNextStep: (players: PlayerSetup[]) => void;
  keyboardControl: boolean;
  onExitKeyboardControl: () => void;
  gameMode: ValuesType<typeof GAME_MODE>;
  context?: 'default' | 'mobilePreview';
}

export default function PlayerSettings({
  songPreview,
  onNextStep,
  keyboardControl,
  onExitKeyboardControl,
  gameMode,
  context = 'default',
}: Props) {
  const [storedPreference] = useSettingValue(MicSetupPreferenceSetting);

  const players = PlayersManager.getPlayers();
  const multipleTracks = players.length === 2 && songPreview.tracksCount > 1;
  const configuredPlayerCount = players.filter(
    (player) => player.input.source !== 'Dummy' && inputSourceListManager.getInputForPlayerSelected(player.input) !== null,
  ).length;
  const canStartSelectedMode = canStartGameMode(
    gameMode,
    songPreview.tracksCount,
    players.length,
    configuredPlayerCount,
  );
  const showMultiplayerWarning = modeNeedsMultiplePlayers(gameMode, songPreview.tracksCount) && !canStartSelectedMode;

  const initialisePlayerSetup = () =>
    players.map((player, index) => ({
      number: player.number,
      track: multipleTracks ? Math.min(index, songPreview.tracksCount - 1) : 0,
    }));

  const [playerSetup, setPlayerSetup] = useState<PlayerSetup[]>(initialisePlayerSetup());
  useEventEffect([gameEvents.playerAdded, gameEvents.playerRemoved], () => setPlayerSetup(initialisePlayerSetup()));

  const playerList = players.map(
    (player, index) => playerSetup.find((setup) => setup.number === player.number) ?? initialisePlayerSetup()[index],
  );

  const playerNames = useMemo<string[]>(
    () => JSON.parse(storage.session.getItem(PLAYER_NAMES_SESSION_STORAGE_KEY)!) ?? [],
    [],
  );

  const updatePlayer = (playerNumber: 0 | 1 | 2 | 3) => (newSetup: PlayerSetup) => {
    setPlayerSetup((current) => current.map((setup) => (setup.number === playerNumber ? newSetup : setup)));
  };

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!showModal) InputManager.startMonitoring();
  }, [showModal]);

  const { register, focusElement } = useKeyboardNav({
    enabled: keyboardControl && !showModal,
    onBackspace: onExitKeyboardControl,
    additionalHelp: { remote: ['select-song'] },
  });

  useEffect(() => {
    RemoteMicManager.broadcast({ t: 'song-selection-player-settings' });
  }, []);

  const startSong = () => {
    if (!canStartSelectedMode) return;

    onNextStep(playerList);
  };
  const areInputsConfigured = !!storedPreference && storedPreference !== 'skip';
  const canContinueToSong = areInputsConfigured && canStartSelectedMode;
  const isMobilePreview = context === 'mobilePreview';
  const showInlineMicCheck = context === 'default';

  return (
    <Root data-context={context} data-modal-open={showModal}>
      <AnimatePresence>
        <SelectInputModal
          open={showModal}
          closeButtonText={canContinueToSong ? 'Continue to the song' : 'Continue to player setup'}
          onClose={() => {
            setShowModal(false);
            if (canContinueToSong) focusElement('play');
          }}
        />
      </AnimatePresence>

      <UnderlyingContent data-hidden={showModal}>
        {showInlineMicCheck && (
          <TopArea data-context={context}>
            <MicCheck names={playerNames} variant="default" />
          </TopArea>
        )}

        <PlayersArea data-context={context}>
          <PlayersCard data-context={context}>
            {playerList.map((setup, index) => (
              <PlayerSettingContainer key={setup.number}>
                <PlayerSettingTitle data-context={context}>Player {index + 1}</PlayerSettingTitle>
                <PlayerContent>
                  <SinglePlayer
                    multipleTracks={multipleTracks}
                    player={PlayersManager.getPlayer(setup.number)}
                    setup={setup}
                    onChange={updatePlayer(setup.number)}
                    playerNames={playerNames}
                    register={register}
                    songPreview={songPreview}
                  />
                </PlayerContent>
              </PlayerSettingContainer>
            ))}
          </PlayersCard>
        </PlayersArea>

        {showMultiplayerWarning && (
          <SetupWarning>
            This mode needs at least two configured players. Use smartphones, multiple microphones or advanced setup.
          </SetupWarning>
        )}

        <ActionsBar data-context={context} data-role="songsettings-actions">
          <ActionsInner>
            {areInputsConfigured && (
              <PrimaryButton
                {...register('play-song-button', startSong, undefined, true, { disabled: !canStartSelectedMode })}
                focused={canStartSelectedMode}
                disabled={!canStartSelectedMode}
                data-mobile={isMobilePreview}>
                Play
              </PrimaryButton>
            )}

            <SecondaryButton
              {...register('select-inputs-button', () => setShowModal(true), undefined, false)}
              data-mobile={isMobilePreview}>
              Setup mics
            </SecondaryButton>
          </ActionsInner>
        </ActionsBar>
      </UnderlyingContent>
    </Root>
  );
}

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const UnderlyingContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;

  &[data-hidden='true'] {
    visibility: hidden;
    pointer-events: none;
  }
`;

const TopArea = styled.div`
  width: 100%;
  margin-bottom: 1rem;

  &[data-context='mobilePreview'] {
    margin-bottom: 0.75rem;
    padding: 0.25rem 0;
  }
`;

const PlayersArea = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  padding-bottom: 1.25rem;

  &[data-context='mobilePreview'] {
    padding-bottom: 0.75rem;
  }
`;

const PlayersCard = styled.div`
  width: 100%;
  color: var(--walky-ink);
  background: var(--walky-canvas);
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  padding: 1rem 1rem 1.25rem;
  backdrop-filter: blur(14px);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);

  &[data-context='mobilePreview'] {
    border-radius: 8px;
    padding: 12px;
  }
`;

const PlayerSettingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  gap: 1rem;
  padding: 0.5rem 0;

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--walky-border);
    margin-bottom: 0.5rem;
    padding-bottom: 1rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const PlayerContent = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const PlayerSettingTitle = styled.span`
  ${typography};
  padding: 0.9rem 0.5rem 0.5rem 0.25rem;
  font-size: 3.3rem;
  width: 18rem;
  text-align: right;
  color: var(--walky-ink);

  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
    padding: 0.25rem 0.25rem 0 0.25rem;
    font-size: clamp(2rem, 5vw, 2.8rem);
  }

  &[data-context='mobilePreview'] {
    padding: 0.25rem 0.25rem 0 0.25rem;
    font-size: clamp(17px, 5vw, 22px);
  }
`;

const SetupWarning = styled.div`
  ${typography};
  margin: 0.25rem 0 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(244, 90, 10, 0.42);
  border-radius: 8px;
  color: var(--walky-ink);
  background: rgba(244, 90, 10, 0.12);
  font-size: clamp(14px, 2.1vw, 18px);
  line-height: 1.35;
`;

const ActionsBar = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 5;
  pointer-events: auto;
  padding: 12px 0 0;
  margin-top: 0.5rem;

  background: linear-gradient(180deg, transparent 0%, var(--walky-surface-translucent) 25%, var(--walky-surface) 100%);
  backdrop-filter: blur(16px);
  border-top: 1px solid var(--walky-border);

  &[data-context='mobilePreview'] {
    padding: 10px 0 calc(env(safe-area-inset-bottom) + 10px);
  }
`;

const ActionsInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrimaryButton = styled(Button)<{ focused?: boolean }>`
  width: 100%;
  max-width: 100%;
  padding: 0.85rem 1.1rem;
  font-size: clamp(16px, 3.2vw, 24px);
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.8rem 1rem;
    font-size: clamp(16px, 4.8vw, 22px);
    align-self: center;
  }

  &[data-mobile='true'] {
    width: 100%;
    max-width: 100%;
    font-size: clamp(16px, 5.2vw, 22px);
    min-height: 46px;
    padding: 0.75rem 1rem;
    border-radius: 8px;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: var(--walky-charcoal);
  color: white;

  &:hover {
    background: #333333;
  }
`;
